/*
 * Product page main gallery.
 *
 * Flickity used to run this, with a crossfade layered on top that absolutely
 * positioned every cell but the first. That combination is unworkable:
 * adaptiveHeight re-measures the selected cell on every select, an absolutely
 * positioned cell measures zero, and the viewport collapsed -- so choosing a
 * thumbnail made the image vanish.
 *
 * The slides are stacked with CSS grid instead. Every slide sits in the same
 * grid cell, so they overlap without leaving normal flow and the container
 * still takes its height from the tallest of them. Nothing has to measure or
 * set a height, and there is no viewport to collapse.
 *
 * Opacity runs through the Web Animations API with fill: forwards, because
 * other scripts on these pages rewrite opacity and restart CSS transitions.
 */
(() => {
  const DURATION = 450;

  const init = () => {
    document.querySelectorAll('.product-section .product__media-list').forEach((list) => {
      if (list.dataset.pdpGallery) return;

      const items = Array.from(list.querySelectorAll('.product__media-item'));
      if (!items.length) return;

      // Flickity rewrites the markup, so hand it back before taking over.
      if (window.Flickity) {
        const existing = window.Flickity.data(list);
        if (existing) existing.destroy();
      }

      list.dataset.pdpGallery = '1';
      list.classList.add('pdp-gallery');
      list.classList.remove('product__media-list--fade');

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let index = 0;

      const render = (animate) => {
        // Read every start value before touching any class, or the ones already
        // updated report their target and the transition is skipped.
        const from = items.map((item) => parseFloat(getComputedStyle(item).opacity) || 0);

        items.forEach((item, i) => {
          item.classList.toggle('is-selected', i === index);
          const target = i === index ? 1 : 0;

          if (!animate || reduceMotion || typeof item.animate !== 'function') {
            item.getAnimations?.().forEach((animation) => animation.cancel());
            item.style.opacity = String(target);
            return;
          }

          if (from[i] === target) return;
          item.getAnimations().forEach((animation) => animation.cancel());
          item.animate([{ opacity: from[i] }, { opacity: target }], {
            duration: DURATION,
            easing: 'ease',
            fill: 'forwards',
          });
        });

        list.dispatchEvent(new CustomEvent('pdp:select', { detail: { index }, bubbles: true }));
      };

      list.pdpSelect = (i) => {
        if (i < 0 || i >= items.length || i === index) return;
        index = i;
        render(true);
      };
      // Steps past slides hidden by the letter filter below.
      const step = (direction) => {
        for (let n = 1; n <= items.length; n++) {
          const i = (index + direction * n + items.length) % items.length;
          if (!items[i].hidden) return list.pdpSelect(i);
        }
      };
      list.pdpNext = () => step(1);
      list.pdpPrevious = () => step(-1);
      list.pdpIndex = () => index;
      list.pdpCount = () => items.filter((item) => !item.hidden).length;

      /*
       * Letter products (2026-10-19): each letter has its own photos, marked
       * with data-letter from the media alt text (", letter A"). Only the
       * chosen letter's photos show; untagged media shows for every letter.
       * Liquid hides the other letters on first render, so their images are
       * never requested until picked.
       */
      const section = list.closest('.product-section') || document;
      const tagged = () =>
        Array.from(
          (list.closest('.product__outer') || section).querySelectorAll('.product__media-sublist > [data-letter]')
        )
          .concat(items, Array.from(section.querySelectorAll('.product-media-modal__content [data-letter]')))
          .filter((el) => el.dataset.letter);
      const filterLetters = () => {
        const checked = section.querySelector('.pdp-letter input:checked');
        if (!checked) return;
        tagged().forEach((el) => {
          el.hidden = el.dataset.letter !== checked.value;
        });
        if (items[index].hidden) {
          const first = items.findIndex((item) => !item.hidden);
          if (first > -1) list.pdpSelect(first);
        }
        list.dispatchEvent(new CustomEvent('pdp:filter', { bubbles: true }));
      };
      if (items.some((item) => item.dataset.letter)) {
        // VariantSelects listens on its own element, so by the time this runs
        // it has already selected the new variant's featured photo.
        section.addEventListener('change', (event) => {
          if (event.target.closest('.pdp-letter')) filterLetters();
        });
        filterLetters();
      }

      render(false);

      // Play the slide's own video, pause everything else.
      list.addEventListener('pdp:select', () => {
        const current = items[index];
        list.querySelectorAll('video').forEach((video) => {
          if (!current.contains(video)) video.pause();
        });
        const video = current.querySelector('video');
        if (video) video.play().catch(() => {});

        list.querySelectorAll('iframe').forEach((frame) => {
          const playing = current.contains(frame);
          const message = frame.classList.contains('js-youtube')
            ? JSON.stringify({ event: 'command', func: playing ? 'playVideo' : 'pauseVideo' })
            : playing
              ? '{"method":"play"}'
              : '{"method":"pause"}';
          try {
            frame.contentWindow.postMessage(message, '*');
          } catch (e) {
            /* cross-origin frame not ready */
          }
        });

        const deferred = current.querySelector('deferred-media button');
        if (deferred) deferred.click();
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', init);
  document.addEventListener('shopify:section:load', () => setTimeout(init, 200));
})();
